import {
  Account,
  AppleToken,
  ConfirmOtpResponse,
  ErrorResponse,
  ForgetPasswordResponse,
  LoginResponse,
  OtpResponse,
  ResumeResponse,
  ServiceResponse,
  StatusResponse,
  ValidateOtpResponse,
  Version,
} from '@bzbs/react-api-client';
import { create } from 'zustand';
import { trackCommonEvents } from '../utils';
import { useAnalyticsStore } from './analytics.store';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';
import { useUserStore } from './user.store';

export type GoogleLoginData = {
  type: 'google';
  idToken: string;
};

export type FacebookLoginData = {
  type: 'facebook';
  accessToken: string;
};

export type AppleLoginData = {
  type: 'apple';
  idToken: string;
  refreshToken: string;
};

export type LineLoginData = {
  type: 'line';
  idToken: string;
  lineAccessToken: string;
  authorizationCode: string;
};

export type ThirdPartyLoginData = GoogleLoginData | FacebookLoginData | AppleLoginData | LineLoginData;

export type AuthState = {
  isInitialized: boolean;
  versionData: Version | null;
  rawVersionData: unknown | null;
  data: LoginResponse | ResumeResponse | null;
  thirdPartyLoginData?: ThirdPartyLoginData;
  isLoading: boolean;
  isLoadingToken: boolean;
  error: ErrorResponse | null;
  token: string | null;
  isLoggedIn: boolean;
  isDeviceLoggedIn: boolean;
};

export type AuthActions = {
  setIsLoading: (isLoading: boolean) => void;
  setIsLoadingToken: (isLoadingToken: boolean) => void;
  setError: (error: ErrorResponse | null) => void;
  setThirdPartyLoginData: (thirdPartyLoginData: ThirdPartyLoginData) => void;
  checkLoggedIn: () => Promise<void>;
  loginWithUsernamePassword: (username: string, password: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithGoogle: (token: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithFacebook: (token: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithApple: (token: string, refreshToken: string) => Promise<ServiceResponse<LoginResponse>>;
  loginWithLine: (
    idToken: string,
    lineAccessToken: string,
    authorizationCode: string
  ) => Promise<ServiceResponse<LoginResponse>>;
  loginWithUUID: () => Promise<ServiceResponse<LoginResponse>>;
  loginWithOtp: (otp: string, refCode: string, contact: string) => Promise<ServiceResponse<LoginResponse>>;
  connectThirdParty: (thirdPartyLoginData: ThirdPartyLoginData) => Promise<ServiceResponse<LoginResponse>>;
  disconnectThirdParty: (thirdParties: {
    facebook?: boolean;
    google?: boolean;
    apple?: boolean;
    line?: boolean;
  }) => Promise<ServiceResponse<Account>>;
  logout: () => Promise<ServiceResponse<unknown>>;
  appleToken: (authorizationCode: string, idToken: string) => Promise<ServiceResponse<AppleToken>>;
  forgetPassword: (
    contact: string,
    type: 'email' | 'contact_number'
  ) => Promise<ServiceResponse<ForgetPasswordResponse>>;
  resetPassword: (
    contact: string,
    otp: string,
    refCode: string,
    password: string
  ) => Promise<ServiceResponse<StatusResponse>>;
  sendOtp: (contact: string, channel: string) => Promise<ServiceResponse<OtpResponse>>;
  validateOtp: (
    otp: string,
    refCode: string,
    contact: string,
    channel: string,
    type: 'email' | 'contact_number'
  ) => Promise<ServiceResponse<ValidateOtpResponse>>;
  confirmOtp: (otp: string, refCode: string, contact: string) => Promise<ServiceResponse<ConfirmOtpResponse>>;
  resume: (clientVersion: string) => Promise<ServiceResponse<ResumeResponse>>;
  version: (clientVersion: string) => Promise<ServiceResponse<Version>>;
  clear: () => void;
};

export type AuthStore = AuthState & AuthActions;

type LoginHandlerParams = {
  loginFn: () => Promise<ServiceResponse<LoginResponse>>;
};

export const useAuthStore = create<AuthStore>((set, get) => {
  // Private helper function to handle common login logic
  const handleLogin = async ({ loginFn }: LoginHandlerParams): Promise<ServiceResponse<LoginResponse>> => {
    const { bzbsService, tokenFunctions, tokenType } = useBuzzebeesAppStore.getState();

    if (!bzbsService || !tokenFunctions) {
      return { type: 'client-error', message: 'Service or auth persistence not configured' };
    }

    set({ isLoading: true, error: null });
    const response = await loginFn();
    set({ isLoading: false });

    if (response.type === 'success') {
      const token = tokenType === 'jwt' ? response.model.jwt : response.model.token;
      tokenFunctions.setToken(token);

      set({
        data: response.model,
        token: response.model.token,
        isLoggedIn: true,
        isDeviceLoggedIn: response.model.account.deviceId != null && response.model.account.deviceId !== '',
        error: null,
      });
      useAnalyticsStore.getState().setUserId(response.model.userId);
      useAnalyticsStore.getState().setCustomDimension(1, useBuzzebeesAppStore.getState().clientVersion);
      useAnalyticsStore.getState().setCustomDimension(2, useBuzzebeesAppStore.getState().appId);

      const userResponse = await useUserStore.getState().fetchUser();
      if (userResponse.type === 'success') {
        useAnalyticsStore.getState().setCustomDimension(3, userResponse.model.Contact_Number ?? '');
        useAnalyticsStore.getState().setCustomDimension(4, userResponse.model.Email ?? '');
      }

      trackCommonEvents.loginSuccess();
    } else {
      set({ error: response });
    }

    return response;
  };

  return {
    isInitialized: false,
    versionData: null,
    rawVersionData: null,
    data: null,
    thirdPartyLoginData: undefined,
    isLoading: false,
    isLoadingToken: false,
    error: null,
    token: null,
    isLoggedIn: false,
    isDeviceLoggedIn: false,

    setIsLoading: (isLoading: boolean) => set({ isLoading }),
    setIsLoadingToken: (isLoadingToken: boolean) => set({ isLoadingToken }),
    setError: (error: ErrorResponse | null) => set({ error }),
    setThirdPartyLoginData: (thirdPartyLoginData: ThirdPartyLoginData) => set({ thirdPartyLoginData }),

    checkLoggedIn: async () => {
      const { tokenFunctions } = useBuzzebeesAppStore.getState();
      if (!tokenFunctions) {
        set({ isLoggedIn: false, isInitialized: true });
        return;
      }

      try {
        set({ isLoadingToken: true });
        const token = await tokenFunctions.getToken();
        if (token) {
          set({ isLoggedIn: true, token });
        } else {
          set({ isLoggedIn: false, token: null });
        }
      } catch (error) {
        set({ isLoggedIn: false, token: null });
      } finally {
        set({ isLoadingToken: false, isInitialized: true });
      }
    },

    loginWithUsernamePassword: async (username: string, password: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();

      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.usernamePasswordLogin({
            username,
            password,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
          }),
      });
    },

    loginWithGoogle: async (token: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      set({ thirdPartyLoginData: { type: 'google', idToken: token } });

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.googleLogin({
            idToken: token,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
          }),
      });
    },

    loginWithFacebook: async (token: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      set({ thirdPartyLoginData: { type: 'facebook', accessToken: token } });

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.facebookLogin({
            accessToken: token,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
          }),
      });
    },

    loginWithApple: async (token: string, refreshToken: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      set({ thirdPartyLoginData: { type: 'apple', idToken: token, refreshToken } });

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.appleLogin({
            idToken: token,
            refreshToken,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
          }),
      });
    },

    loginWithLine: async (idToken: string, lineAccessToken: string, authorizationCode: string) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      set({ thirdPartyLoginData: { type: 'line', idToken, lineAccessToken, authorizationCode } });

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.lineLogin({
            idToken,
            lineAccessToken,
            authorizationCode,
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
          }),
      });
    },

    loginWithUUID: async () => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.deviceLogin({
            appId,
            uuid,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
          }),
      });
    },

    loginWithOtp: async (otp: string, refCode: string, contact: string) => {
      const { appId, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      return handleLogin({
        loginFn: () =>
          bzbsService.authApi.deviceLogin({
            appId,
            uuid: contact,
            deviceToken: fcmToken,
            clientVersion,
            os,
            platform,
            deviceLocale: localeId.toString(),
            deviceNotificationEnabled,
            macAddress,
            otp,
            refcode: refCode,
            contact_number: contact,
          }),
      });
    },

    connectThirdParty: async (thirdPartyLoginData: ThirdPartyLoginData) => {
      const { appId, uuid, os, platform, deviceNotificationEnabled, clientVersion, fcmToken, macAddress } =
        useBuzzebeesAppStore.getState();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      let response: ServiceResponse<LoginResponse>;

      switch (thirdPartyLoginData.type) {
        case 'google':
          response = await handleLogin({
            loginFn: () =>
              bzbsService.authApi.connectGoogle({
                idToken: thirdPartyLoginData.idToken,
                appId,
                uuid,
                deviceToken: fcmToken,
                clientVersion,
                os,
                platform,
                deviceLocale: localeId.toString(),
                deviceNotificationEnabled,
                macAddress,
              }),
          });
          break;
        case 'facebook':
          response = await handleLogin({
            loginFn: () =>
              bzbsService.authApi.connectFacebook({
                accessToken: thirdPartyLoginData.accessToken,
                appId,
                uuid,
                deviceToken: fcmToken,
                clientVersion,
                os,
                platform,
                deviceLocale: localeId.toString(),
                deviceNotificationEnabled,
                macAddress,
              }),
          });
          break;
        case 'apple':
          response = await handleLogin({
            loginFn: () =>
              bzbsService.authApi.connectApple({
                idToken: thirdPartyLoginData.idToken,
                refreshToken: thirdPartyLoginData.refreshToken,
                appId,
                uuid,
                deviceToken: fcmToken,
                clientVersion,
                os,
                platform,
                deviceLocale: localeId.toString(),
                deviceNotificationEnabled,
                macAddress,
              }),
          });
          break;
        case 'line':
          response = await handleLogin({
            loginFn: () =>
              bzbsService.authApi.connectLine({
                idToken: thirdPartyLoginData.idToken,
                lineAccessToken: thirdPartyLoginData.lineAccessToken,
                authorizationCode: thirdPartyLoginData.authorizationCode,
                appId,
                uuid,
                deviceToken: fcmToken,
                clientVersion,
                os,
                platform,
                deviceLocale: localeId.toString(),
                deviceNotificationEnabled,
                macAddress,
              }),
          });
      }

      if (response.type === 'success') {
        set({ thirdPartyLoginData: undefined });
      }

      return response;
    },

    disconnectThirdParty: async (thirdParties: {
      facebook?: boolean;
      google?: boolean;
      apple?: boolean;
      line?: boolean;
    }) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.disconnect({
        facebook: thirdParties.facebook,
        google: thirdParties.google,
        apple: thirdParties.apple,
        line: thirdParties.line,
      });
      set({ isLoading: false });
      if (response.type === 'success') {
        useUserStore.setState({
          thirdPartyConnection: {
            facebook: response.model.facebookId != null && response.model.facebookId !== '',
            google: response.model.googleId != null && response.model.googleId !== '',
            apple: response.model.appleId != null && response.model.appleId !== '',
            line: response.model.lineUserId != null && response.model.lineUserId !== '',
          },
        });
      }

      return response;
    },

    logout: async () => {
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service or auth persistence not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.logout({ uuid });
      set({ isLoading: false });

      get().clear();

      return response;
    },

    appleToken: async (authorizationCode: string, idToken: string) => {
      const { appId, os, platform, clientVersion, macAddress } = useBuzzebeesAppStore.getState();
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.appleToken({
        authorizationCode,
        idToken,
        appId,
        os,
        platform,
        macAddress,
        clientVersion,
      });
      set({ isLoading: false });
      return response;
    },

    forgetPassword: async (contact: string, type: 'email' | 'contact_number') => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.forgetPassword({ contact, type });
      set({ isLoading: false });
      return response;
    },

    resetPassword: async (contact: string, otp: string, refCode: string, password: string) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.resetPassword({
        contact,
        otp,
        refCode,
        newPassword: password,
      });
      set({ isLoading: false });
      return response;
    },

    sendOtp: async (contact: string, channel: string) => {
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.otp({
        uuid,
        appId,
        contactNumber: contact,
        channel,
      });
      set({ isLoading: false });
      return response;
    },

    validateOtp: async (
      otp: string,
      refCode: string,
      contact: string,
      channel: string,
      type: 'email' | 'contact_number'
    ) => {
      const appId = useBuzzebeesAppStore.getState().appId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.validateOtp({
        appId,
        otp,
        refCode,
        contactNumber: contact,
        use: false,
        channel,
        type,
      });
      set({ isLoading: false });
      return response;
    },

    confirmOtp: async (otp: string, refCode: string, contact: string) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.confirmOtp({
        otp,
        refCode,
        contactNumber: contact,
      });
      set({ isLoading: false });
      return response;
    },

    resume: async (clientVersion: string) => {
      const { uuid, appId, os, platform, fcmToken, macAddress } = useBuzzebeesAppStore.getState();
      const { tokenFunctions, tokenType } = useBuzzebeesAppStore.getState();
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService || !tokenFunctions) {
        return { type: 'client-error', message: 'Service or auth persistence not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.resume({
        uuid,
        deviceAppId: appId,
        os,
        platform,
        deviceNotificationEnabled: true,
        clientVersion,
        deviceToken: fcmToken,
        macAddress,
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        const token = tokenType === 'jwt' ? response.model.jwt! : response.model.token!;
        tokenFunctions.setToken(token);
        set({
          data: response.model,
          error: null,
        });
      } else {
        set({ error: response });
      }

      return response;
    },

    version: async (clientVersion: string) => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.versionRaw(clientVersion);
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ versionData: response.model, rawVersionData: response.model });
      }

      return response;
    },

    clear: () => {
      const { tokenFunctions } = useBuzzebeesAppStore.getState();
      tokenFunctions?.removeToken();
      set({
        data: null,
        thirdPartyLoginData: undefined,
        versionData: null,
        isLoading: false,
        error: null,
        token: null,
        isLoggedIn: false,
        isDeviceLoggedIn: false,
      });
    },
  };
});
